import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Award, CreditCard, Bell, ArrowRight, ClipboardEdit, FileText, TrendingUp } from "lucide-react";
import apiClient from "@/services/apiClient";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { useStudentCourses } from "@/hooks/useStudentCourses";
import { parseCalendarDate } from "@/utils/dateUtils";

const formatAudCurrency = (amount) => `$${Number(amount || 0).toFixed(2)} AUD`;

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { courses } = useStudentCourses();

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
        const response = await apiClient.get(`${apiBaseUrl}/StudentDashboard/dashboard`);
        setDashboard(response.data);
      } catch (requestError) {
        console.error("Failed to load student dashboard", requestError);
        setError(requestError.response?.data?.message || "Unable to load your dashboard.");
      }
    };

    loadDashboard();
  }, []);

  if (!dashboard && !error) {
    return <div className="py-12 text-center text-sm text-slate-500">Loading your dashboard...</div>;
  }

  if (error) {
    return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }

  const nextClass = dashboard.nextClass;
  const hasNextClass = Number(nextClass?.classId) > 0;
  const nextClassDate = nextClass?.classDate ? parseCalendarDate(nextClass.classDate) : null;
  const notifications = Array.isArray(dashboard.notifications) ? dashboard.notifications : [];
  const upcomingClassCount = Number(dashboard.upcomingClassCount) || (hasNextClass ? 1 : 0);
  const profileCompletion = Number(dashboard.profileCompletion) || 0;
  const totalPaid = Number(dashboard.totalPaid) || 0;
  const inProgressCourses = courses.filter((course) => course.hasPendingAction || !course.isCompleted);
  const primaryCourse = inProgressCourses[0];

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      <div className="wc-card p-6 bg-gradient-to-r from-[#0A2540] to-[#123057] text-white border-none">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-orange-300 font-semibold">Welcome back</div>
            <h1 className="text-3xl font-display font-extrabold mt-1">{dashboard.firstName} {dashboard.lastName}</h1>
            <p className="text-slate-300 text-sm mt-1">
              {hasNextClass && nextClassDate
                ? `Your next class is on ${nextClassDate.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })} at ${nextClass.location || "your training centre"}`
                : "You have no upcoming classes."}
            </p>
          </div>
          <Link to="/student/my-courses" className="wc-btn-primary text-sm" data-testid="dash-enrol-cta"><ClipboardEdit className="w-4 h-4" /> Complete your Enrollment</Link>
        </div>
      </div>

      {primaryCourse && (
        <div className="wc-card border-orange-200 p-5" data-testid="dashboard-continue-course">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-orange-600 font-semibold">Continue Your Course</div>
              <h2 className="mt-1 font-display text-xl font-extrabold text-[#0A2540]">{primaryCourse.title}</h2>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                <span>Enrolment status: <span className="font-semibold text-slate-700">{primaryCourse.workFlowStatusName}</span></span>
                <span>Booking: <span className="font-semibold text-slate-700">{primaryCourse.bookingNumber || "-"}</span></span>
              </div>
              <div className="mt-3 max-w-xl">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold uppercase tracking-wide text-slate-500">Progress</span>
                  <span className="font-bold text-orange-600">{primaryCourse.progress}%</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.max(0, Math.min(100, primaryCourse.progress))}%` }} />
                </div>
              </div>
              <div className="mt-2 text-sm text-slate-600">Next step: <span className="font-semibold text-[#0A2540]">{primaryCourse.nextStep || "Continue your course"}</span></div>
            </div>
            <Link to={inProgressCourses.length > 1 ? "/student/my-courses" : `/student/caurse-progress/${primaryCourse.studentBookingId || primaryCourse.id}`} className="wc-btn-primary justify-center text-sm">
              {inProgressCourses.length > 1 ? "View My Courses" : "Continue"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarDays} label="Upcoming classes" value={String(upcomingClassCount)} hint={nextClassDate ? `Next: ${nextClassDate.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}` : "No upcoming class"} />
        <StatCard icon={Award} label="Certificates" value={String(dashboard.certificateCount || 0)} hint="Downloadable" />
        <StatCard icon={CreditCard} label="Total paid" value={formatAudCurrency(totalPaid)} hint="All time" />
        <StatCard icon={TrendingUp} label="Profile" value={`${profileCompletion}%`} hint="Complete profile" progress={profileCompletion} />
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="wc-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-extrabold text-[#0A2540] text-lg">Upcoming courses</div>
            <Link to="/student/bookings" className="text-xs text-orange-600 font-semibold hover:underline">View all</Link>
          </div>
          {hasNextClass ? (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 flex flex-col sm:flex-row gap-4 items-start">
              <div className="w-16 h-16 rounded-lg bg-[#0A2540] text-white flex flex-col items-center justify-center shrink-0">
                <div className="text-[10px] uppercase font-semibold text-orange-300">{nextClassDate ? nextClassDate.toLocaleDateString("en-AU", { month: "short" }) : ""}</div>
                <div className="text-2xl font-display font-extrabold leading-none">{nextClassDate?.getDate() || ""}</div>
              </div>
              <div className="flex-1">
                <div className="font-semibold">{nextClass.courseName} {nextClass.courseCode ? `- ${nextClass.courseCode}` : ""}</div>
                <div className="text-sm text-slate-600 mt-1">{nextClass.time} - {nextClass.location}</div>
                <div className="text-xs text-slate-500 mt-1">Trainer: {nextClass.trainer || "TBA"}</div>
                <span className="mt-2 inline-block wc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">{nextClass.status || "Confirmed"}</span>
              </div>
            </div>
          ) : <div className="text-sm text-slate-500 py-8 text-center">No upcoming classes</div>}
        </div>

        <div className="wc-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-extrabold text-[#0A2540] text-lg flex items-center gap-2"><Bell className="w-4 h-4" />Notifications</div>
            <Link to="/student/notifications" className="text-xs text-orange-600 font-semibold hover:underline">All</Link>
          </div>
          <div className="space-y-3">
            {notifications.slice(0, 3).map((notification) => (
              <div key={notification.notificationId} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${notification.type === "success" ? "bg-emerald-500" : "bg-blue-500"}`} />
                <div className="flex-1"><div className="text-sm font-semibold">{notification.title}</div><div className="text-xs text-slate-500">{notification.body}</div><div className="text-[10px] text-slate-400 mt-0.5">{notification.time}</div></div>
              </div>
            ))}
            {notifications.length === 0 ? <div className="py-3 text-center text-sm text-slate-500">No notifications</div> : null}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { to: "/student/profile", icon: FileText, label: "Edit profile" },
          { to: "/student/bookings", icon: CalendarDays, label: "My bookings" },
          { to: "/student/certificates", icon: Award, label: "Certificates" },
          { to: "/student/documents", icon: FileText, label: "Documents" },
          { action: () => setShowChangePassword(true), icon: FileText, label: "Change password" },
        ].map((quickLink) => (
          <button key={quickLink.label} onClick={() => quickLink.to ? navigate(quickLink.to) : quickLink.action()} className="wc-card flex items-center gap-3 group text-left" data-testid={`quick-${quickLink.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-10 h-10 rounded-lg bg-[#0A2540] group-hover:bg-orange-500 text-white flex items-center justify-center transition-colors"><quickLink.icon className="w-4 h-4" /></div>
            <div className="flex-1 font-semibold text-sm text-[#0A2540]">{quickLink.label}</div>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all" />
          </button>
        ))}
      </div>
      {showChangePassword && <ChangePasswordDialog endpoint="/StudentAccount/ChangePassword" onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, progress }) {
  return (
    <div className="wc-card">
      <div className="flex items-start justify-between mb-3"><div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><Icon className="w-5 h-5" /></div></div>
      <div className="text-3xl font-display font-extrabold text-[#0A2540]">{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mt-1">{label}</div>
      {progress !== undefined ? <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500" style={{ width: `${progress}%` }} /></div> : <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
